/**
 * MentionAutocomplete Component
 * Feature: 036-markdown-critique-comments (User Story 3)
 * T032, T035, T036: Autocomplete dropdown for @-mentions with keyboard navigation
 *
 * Displays a dropdown list of suggestions when user types "@" in a text field.
 * Supports keyboard navigation (arrows, Enter, Escape) and mouse selection.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { MentionAutocompleteOption } from '../hooks/useMentionAutocomplete';
import './MentionAutocomplete.css';

// ============================================================================
// Types
// ============================================================================

export interface MentionAutocompleteProps {
  /** Available autocomplete options */
  options: MentionAutocompleteOption[];

  /** Loading state */
  isLoading?: boolean;

  /** Callback when user selects an option */
  onSelect: (option: MentionAutocompleteOption) => void;

  /** Callback when user cancels (Escape key) */
  onCancel?: () => void;

  /** Position coordinates for dropdown (relative to trigger) */
  position?: {
    top: number;
    left: number;
  };

  /** Whether the dropdown is visible */
  visible: boolean;

  /** Optional CSS class */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export function MentionAutocomplete({
  options,
  isLoading = false,
  onSelect,
  onCancel,
  position,
  visible,
  className = '',
}: MentionAutocompleteProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Reset selection when options change
  useEffect(() => {
    setSelectedIndex(0);
    optionRefs.current = optionRefs.current.slice(0, options.length);
  }, [options]);

  // Scroll selected option into view
  useEffect(() => {
    if (selectedIndex >= 0 && selectedIndex < optionRefs.current.length) {
      optionRefs.current[selectedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex]);

  // Keyboard navigation (T036)
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!visible || options.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(prev => (prev + 1) % options.length);
          break;

        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(prev => (prev - 1 + options.length) % options.length);
          break;

        case 'Enter':
          // Only prevent default if there's a selection to commit
          if (options[selectedIndex]) {
            event.preventDefault();
            onSelect(options[selectedIndex]);
          }
          break;

        case 'Tab':
          event.preventDefault();
          if (options[selectedIndex]) {
            onSelect(options[selectedIndex]);
          }
          break;

        case 'Escape':
          event.preventDefault();
          onCancel?.();
          break;

        default:
          // Allow other keys to pass through (for typing)
          break;
      }
    },
    [visible, options, selectedIndex, onSelect, onCancel]
  );

  // Attach/detach keyboard listener
  useEffect(() => {
    if (visible) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [visible, handleKeyDown]);

  // Don't render if not visible
  if (!visible) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <div
        ref={dropdownRef}
        className={`mention-autocomplete ${className}`}
        style={position}
        data-testid="mention-autocomplete"
      >
        <div className="mention-autocomplete-loading">Loading...</div>
      </div>
    );
  }

  // No options
  if (options.length === 0) {
    return (
      <div
        ref={dropdownRef}
        className={`mention-autocomplete ${className}`}
        style={position}
        data-testid="mention-autocomplete"
      >
        <div className="mention-autocomplete-empty">No suggestions</div>
      </div>
    );
  }

  // Render options list
  return (
    <div
      ref={dropdownRef}
      className={`mention-autocomplete ${className}`}
      style={position}
      data-testid="mention-autocomplete"
      role="listbox"
      aria-label="Mention autocomplete suggestions"
    >
      {options.map((option, index) => (
        <div
          key={option.objectId}
          ref={el => {
            optionRefs.current[index] = el;
          }}
          className={`mention-autocomplete-option ${
            index === selectedIndex ? 'selected' : ''
          }`}
          role="option"
          aria-selected={index === selectedIndex}
          onClick={() => onSelect(option)}
          onMouseEnter={() => setSelectedIndex(index)}
          data-testid={`mention-option-${option.objectId}`}
        >
          {/* Photo thumbnail (if available) */}
          {option.thumbnailUrl && (
            <img
              src={option.thumbnailUrl}
              alt=""
              className="mention-autocomplete-thumbnail"
              loading="lazy"
            />
          )}

          {/* Shape tool icon (050-critique-markup-integration) */}
          {option.type === 'shapes' && option.icon && (
            <div className="mention-autocomplete-icon">
              {React.createElement(option.icon, { className: 'w-5 h-5' })}
            </div>
          )}

          {/* Label and object ID */}
          <div className="mention-autocomplete-text">
            <div className="mention-autocomplete-label">{option.label}</div>
            <div className="mention-autocomplete-id">@{option.objectId}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Helper: Calculate dropdown position
// ============================================================================

export interface CalculatePositionParams {
  /** Textarea or input element */
  inputElement: HTMLTextAreaElement | HTMLInputElement;

  /** Start position of "@" character in text */
  triggerPos: number;

  /** Approximate character width (px) */
  charWidth?: number;

  /** Approximate line height (px) */
  lineHeight?: number;
}

/**
 * Calculate position for autocomplete dropdown relative to cursor
 *
 * @param params - Position calculation parameters
 * @returns CSS position (top, left) in pixels
 */
export function calculateDropdownPosition(
  params: CalculatePositionParams
): { top: number; left: number } {
  const {
    inputElement,
    triggerPos,
    charWidth = 8, // Approximate monospace char width
    lineHeight = 20, // Approximate line height
  } = params;

  const rect = inputElement.getBoundingClientRect();
  const text = inputElement.value.substring(0, triggerPos);

  // Count newlines before trigger to estimate line number
  const lines = text.split('\n').length;

  // Estimate column position on current line
  const lastLineLength = text.split('\n').pop()?.length || 0;

  // Calculate position relative to viewport
  const top = rect.top + lines * lineHeight + lineHeight; // Below current line
  const left = rect.left + lastLineLength * charWidth;

  return { top, left };
}
